/* eslint-disable react/prop-types */
/* eslint-disable simple-import-sort/imports */
import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import { styled } from 'styled-components';

import { dataInfoFaqs } from 'pages/Home/Sections/InfoFaqs/data-InfoFaqs';

import { Reveal } from '../components/Motion';
import { Container, Eyebrow, Lead, SectionHead, Title, v2 } from '../v2';

const items = dataInfoFaqs.info;

/* Seconds each feature stays active before auto-advancing. */
const ROTATE_SECONDS = 9;

const Wrapper = styled.section`
  position: relative;
  background: ${v2.bgSoft};
  border-top: 1px solid ${v2.border};
  padding: clamp(80px, 10vw, 130px) 0;
`;

/* ---------- Desktop: list + image, auto-rotating ---------- */

const DesktopGrid = styled(Container)`
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: clamp(40px, 5vw, 90px);
  align-items: start;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const ListItem = styled.button`
  position: relative;
  text-align: left;
  padding: 18px 0 18px 28px;
  border-left: 2px solid
    ${({ $active }) => ($active ? 'transparent' : 'rgba(255,255,255,0.08)')};
  transition: border-color 0.35s ease;

  h3 {
    font-family: ${v2.fontDisplay};
    font-size: ${({ $active }) => ($active ? '1.45rem' : '1.15rem')};
    font-weight: 700;
    letter-spacing: -0.02em;
    color: ${({ $active }) => ($active ? v2.text : v2.textFaint)};
    margin: 0;
    transition:
      color 0.35s ease,
      font-size 0.35s ease;
  }

  p {
    font-family: ${v2.fontBody};
    font-size: 15px;
    line-height: 1.6;
    color: ${v2.textMuted};
    margin: ${({ $active }) => ($active ? '10px 0 0' : '0')};
    max-height: ${({ $active }) => ($active ? '200px' : '0')};
    opacity: ${({ $active }) => ($active ? 1 : 0)};
    overflow: hidden;
    transition:
      max-height 0.45s ease,
      opacity 0.45s ease,
      margin 0.45s ease;
  }

  &:hover h3 {
    color: ${({ $active }) => ($active ? v2.text : v2.textMuted)};
  }
`;

/* Vertical progress line on the active item, fills over ROTATE_SECONDS. */
const ProgressTrack = styled.span`
  position: absolute;
  top: 0;
  bottom: 0;
  left: -2px;
  width: 2px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
`;

const ProgressFill = styled(motion.span)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  background: linear-gradient(180deg, ${v2.blue}, ${v2.orange});
  transform-origin: top;
`;

/* Matches the original section: near-square images (795x720) shown
   uncropped on a light card, sticky next to the list. */
const ShotStage = styled.div`
  position: sticky;
  top: 110px;
  width: 100%;
  max-width: 580px;
  aspect-ratio: 795 / 720;
  justify-self: center;
`;

const ShotCard = styled(motion.div)`
  position: absolute;
  inset: 0;
  border-radius: 20px;
  border: 1px solid ${v2.borderStrong};
  background: ${v2.bgElevated};
  box-shadow:
    0 40px 90px rgba(0, 0, 0, 0.5),
    0 0 60px rgba(30, 150, 252, 0.1);
  overflow: hidden;
  padding-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

/* ---------- Mobile: stacked cards ---------- */

const MobileStack = styled(Container)`
  display: none;

  @media (max-width: 1024px) {
    display: flex;
    flex-direction: column;
    gap: 28px;
  }
`;

const MobileCard = styled.div`
  border-radius: 20px;
  border: 1px solid ${v2.border};
  background: ${v2.surface};
  overflow: hidden;

  img {
    width: 100%;
    display: block;
    border-bottom: 1px solid ${v2.border};
  }

  > div {
    padding: 24px;
  }

  h3 {
    font-family: ${v2.fontDisplay};
    font-size: 1.3rem;
    font-weight: 700;
    color: ${v2.text};
    margin: 0 0 10px;
  }

  p {
    font-family: ${v2.fontBody};
    font-size: 15px;
    line-height: 1.65;
    color: ${v2.textMuted};
    margin: 0;
  }
`;

const FeaturesV2 = () => {
  const gridRef = useRef(null);
  const [active, setActive] = useState(0);
  const inView = useInView(gridRef, { amount: 0.35 });

  // Auto-advance while the section is visible; selecting an item resets
  // the timer because `active` changes.
  useEffect(() => {
    if (!inView) return undefined;
    const timer = setTimeout(
      () => setActive((value) => (value + 1) % items.length),
      ROTATE_SECONDS * 1000
    );
    return () => clearTimeout(timer);
  }, [inView, active]);

  return (
    <Wrapper id="features">
      <Container>
        <SectionHead $left>
          <Reveal>
            <Eyebrow>Features</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <Title>
              Das alles kann <span className="grad">FOXI</span>
            </Title>
          </Reveal>
          <Reveal delay={0.2}>
            <Lead>
              Von der Eingabe bis zur Übertragung in Dein PVS — FOXI unterstützt
              Dein Team mit einer kompletten Abrechnung und Dokumentation.
            </Lead>
          </Reveal>
        </SectionHead>
      </Container>

      <DesktopGrid ref={gridRef}>
        <List>
          {items.map((item, index) => {
            const isActive = index === active;
            return (
              <ListItem
                key={item.title}
                type="button"
                $active={isActive}
                onClick={() => setActive(index)}
              >
                {isActive && (
                  <ProgressTrack>
                    <ProgressFill
                      key={`${active}-${inView}`}
                      initial={{ height: '0%' }}
                      animate={{ height: inView ? '100%' : '0%' }}
                      transition={{
                        duration: ROTATE_SECONDS,
                        ease: 'linear',
                      }}
                    />
                  </ProgressTrack>
                )}
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </ListItem>
            );
          })}
        </List>
        <ShotStage>
          <AnimatePresence mode="popLayout">
            <ShotCard
              key={active}
              initial={{ opacity: 0, y: 36, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -28, scale: 0.98 }}
              transition={{ duration: 0.55, ease: v2.ease }}
            >
              <img
                src={items[active].image}
                alt={items[active].title}
                loading="lazy"
              />
            </ShotCard>
          </AnimatePresence>
        </ShotStage>
      </DesktopGrid>

      <MobileStack>
        {items.map((item, index) => (
          <Reveal key={item.title} delay={index * 0.05}>
            <MobileCard>
              <img src={item.image} alt={item.title} loading="lazy" />
              <div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            </MobileCard>
          </Reveal>
        ))}
      </MobileStack>
    </Wrapper>
  );
};

export default FeaturesV2;
